'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { formatEstimateMoney } from '@/shared/lib/utils';
import type { Estimate } from '../api/estimate-service';

interface EstimateTotalsProps {
  estimate: Estimate;
  /** The currency / units tag-toggles, pinned to the trailing edge of the
   *  heading. Rendered by the editor (it owns the tag wiring) and passed in so
   *  the heading + tags live inside this card's header. */
  titleTags?: ReactNode;
}

/**
 * Totals summary card at the TOP of the estimate. It carries the page heading
 * ("Cost estimate") as its own header — the facade work area rides there as a
 * subtitle (context for the whole estimate, not a figure in the money column),
 * with the display tags pinned trailing. Below the heading: the
 * materials/labor sub-totals over a divider, then the grand total.
 *
 * The money figures are SUMMED from the items in the query cache — item edits
 * are written to the cache optimistically per keystroke (see
 * `applyItemDraft` in use-estimate-items), so the sub-totals and grand total
 * recompute interactively, exactly like the row totals. The
 * server-authoritative estimate replaces the cache on every PATCH settle /
 * refetch / WS reconcile, so the sums never drift from the API for long.
 */
export function EstimateTotals({ estimate, titleTags }: EstimateTotalsProps) {
  const t = useTranslations('common.estimate.editor');

  const materialsTotal = estimate.items
    .filter((i) => i.section === 'materials')
    .reduce((sum, i) => sum + i.line_total, 0);
  const laborTotal = estimate.items
    .filter((i) => i.section === 'labor')
    .reduce((sum, i) => sum + i.line_total, 0);

  const areaUnitLabel =
    estimate.facade_area_unit === 'ft2' ? t('units.ft2') : t('units.m2');

  return (
    <SettingsCard surfaceClassName="bg-estimate-paper">
      <div className="space-y-1 px-4 pt-3">
        {/* Page heading lives INSIDE the first estimate block (no separate
            title row above it). The facade work area is the heading's subtitle;
            the display tags (currency / units) pin to the trailing edge. */}
        <div className="flex items-start gap-2 pb-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-card-title text-foreground">{t('title')}</h2>
            {estimate.facade_area != null && (
              <p className="text-card-meta text-muted-foreground">
                {t('facadeWorkArea')} · {estimate.facade_area} {areaUnitLabel}
              </p>
            )}
          </div>
          {titleTags}
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-card-meta text-foreground">
            {t('sections.materials')}
          </span>
          <span className="text-card-meta whitespace-nowrap font-mono text-foreground tabular-nums">
            {formatEstimateMoney(materialsTotal, estimate.currency)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-card-meta text-foreground">
            {t('sections.labor')}
          </span>
          <span className="text-card-meta whitespace-nowrap font-mono text-foreground tabular-nums">
            {formatEstimateMoney(laborTotal, estimate.currency)}
          </span>
        </div>
      </div>

      {/* Grand total — a step larger than the sub-totals for hierarchy, but at
          a REGULAR weight (not the bold/semibold headline treatment) so the
          card holds fewer weight variations. Extra bottom margin gives a more
          premium, less cramped close to the headline block. */}
      <div className="mx-4 mb-3 mt-2 flex items-center justify-between border-t border-border/40 py-3">
        <span className="text-card-body text-foreground">{t('grandTotal')}</span>
        <span className="text-card-body whitespace-nowrap font-mono text-foreground tabular-nums">
          {formatEstimateMoney(materialsTotal + laborTotal, estimate.currency)}
        </span>
      </div>
    </SettingsCard>
  );
}
